import React, { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { AboutLink } from "@/pages/About";
import { AcceptTerms, LegalLinks } from "@/components/Legal";
import { toast } from "sonner";
import { AuthLayout } from "@/components/AuthLayout";
import { Input, Label } from "@/components/ui-kit";
import { OtpStep } from "@/components/OtpStep";
import { ResetStep } from "@/components/ResetStep";
import { api, errorText } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";
import { useAuth, homeFor } from "@/contexts/AuthContext";

type Mode = "login" | "register" | "otp" | "reset";

export default function CustomerLogin() {
  const { t } = usePrefs();
  const { user, refreshMe } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>("login");
  const [f, setF] = useState({ name: "", phone: "", password: "", confirm: "" });
  const [agree, setAgree] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  if (user) return <Navigate to={localStorage.getItem("wn_after_login") || homeFor(user.role)} replace />;

  const done = async () => {
    await refreshMe();
    const to = localStorage.getItem("wn_after_login");
    localStorage.removeItem("wn_after_login");
    navigate(to || "/app", { replace: true });
  };
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (mode === "register" && f.password !== f.confirm) return setError(t("sec.mismatch"));
    setBusy(true);
    try {
      if (mode === "login") {
        await api.post("/auth/customer/login", { phone: f.phone, password: f.password });
        await done();
      } else {
        await api.post("/auth/customer/register", { name: f.name, phone: f.phone, password: f.password, accept_terms: agree });
        toast.success(t("cl.registered"));
        setMode("otp");
      }
    } catch (err: any) {
      if (err?.response?.data?.detail === "Account not activated") return setMode("otp");
      setError(errorText(err, t));
    } finally {
      setBusy(false);
    }
  };
  const field = (k: keyof typeof f, label: string, props: Record<string, unknown>) => (
    <div className="space-y-1.5">
      <Label htmlFor={`cl-${k}`}>{t(label)}</Label>
      <Input id={`cl-${k}`} required value={f[k]} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [k]: e.target.value })} data-testid={`customer-${k}-input`} className="h-12 rounded-2xl" {...props} />
    </div>
  );

  if (mode === "otp") return (
    <AuthLayout title={t("cl.otpTitle")} sub={t("cl.otpSub")}>
      <OtpStep phone={f.phone} onDone={done} onBack={() => setMode("login")} />
    </AuthLayout>
  );

  if (mode === "reset") return (
    <AuthLayout title={t("rs.title")} sub={t("rs.sub")}>
      <ResetStep phone={f.phone} onDone={done} onBack={() => setMode("login")} />
    </AuthLayout>
  );

  return (
    <AuthLayout title={t(mode === "login" ? "auth.customerTitle" : "cl.registerTitle")} sub={t(mode === "login" ? "auth.customerSub" : "cl.registerSub")}>
      <div className="grid grid-cols-2 gap-1 rounded-full bg-muted p-1" role="tablist">
        {(["login", "register"] as const).map((m) => (
          <button key={m} type="button" role="tab" aria-selected={mode === m} onClick={() => { setMode(m); setError(""); }} data-testid={`customer-tab-${m}`}
            className={`h-10 rounded-full text-sm font-bold transition-[background-color] ${mode === m ? "bg-background shadow-sm" : "text-muted-foreground"}`}>{t(`cl.tab.${m}`)}</button>
        ))}
      </div>
      <form onSubmit={submit} className="space-y-4" data-testid={`customer-${mode}-form`}>
        {mode === "register" && field("name", "cl.name", { autoComplete: "name", minLength: 2, maxLength: 80 })}
        {field("phone", "cl.phone", { dir: "ltr", type: "tel", inputMode: "tel", autoComplete: "tel", placeholder: "07XXXXXXXXX" })}
        {field("password", "auth.password", { dir: "ltr", type: "password", autoComplete: mode === "login" ? "current-password" : "new-password", minLength: mode === "login" ? 1 : 8, maxLength: 128 })}
        {mode === "register" && field("confirm", "sec.confirm", { dir: "ltr", type: "password", autoComplete: "new-password", minLength: 8, maxLength: 128 })}
        {mode === "register" && <AcceptTerms checked={agree} onChange={setAgree} />}
        {error && <p data-testid="customer-auth-error" className="text-sm text-primary font-semibold">{error}</p>}
        <button disabled={busy} data-testid="customer-auth-submit" className="w-full h-12 rounded-full bg-primary text-primary-foreground font-bold disabled:opacity-60 active:scale-[0.98] transition-transform">
          {busy ? t("common.loading") : t(mode === "login" ? "auth.submit" : "cl.create")}
        </button>
      </form>
      <div className="text-sm space-y-2">
        {mode === "login" && <button type="button" onClick={() => { setError(""); setMode("reset"); }} data-testid="customer-forgot-password" className="block font-bold text-primary">{t("rs.link")}</button>}
        {mode === "login" && <button type="button" onClick={() => f.phone ? setMode("otp") : setError(t("cl.needPhone"))} data-testid="customer-have-code" className="block text-muted-foreground font-semibold">{t("cl.haveCode")}</button>}
        <Link to="/partner/login" data-testid="goto-partner-login-link" className="block text-muted-foreground font-semibold">{t("auth.partnerLink")}</Link>
        <AboutLink />
        <LegalLinks />
      </div>
    </AuthLayout>
  );
}
